import { useState } from 'react';
import { Plus, Tag, Percent, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { API } from '../../config/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const EMPTY_FORM = {
  code: '',
  discount_type: 'percentage',
  discount_value: 10,
  max_uses: null,
  expires_at: '',
  min_purchase: 0,
};

export function DiscountsPanel({ discountCodes, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const resetForm = () => { setForm(EMPTY_FORM); setEditing(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`${API}/discounts/${editing.id}`, form, { withCredentials: true });
        toast.success('Discount code updated!');
      } else {
        await api.post(`${API}/discounts`, form, { withCredentials: true });
        toast.success('Discount code created!');
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save discount code');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await api.delete(`${API}/discounts/${id}`, { withCredentials: true });
      toast.success('Discount code deleted!');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete discount code');
    }
  };

  const toggleActive = async (discount) => {
    try {
      await api.put(`${API}/discounts/${discount.id}`, { is_active: !discount.is_active }, { withCredentials: true });
      toast.success(`Discount ${discount.is_active ? 'deactivated' : 'activated'}!`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to update discount');
    }
  };

  return (
    <div className="space-y-6" data-testid="discounts-content">
      <div className="flex items-center justify-between">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">DISCOUNT CODES</h2>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="add-discount-btn">
              <Plus className="w-4 h-4 mr-2" /> ADD CODE
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-black uppercase">
                {editing ? 'EDIT DISCOUNT' : 'ADD DISCOUNT CODE'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="overline">Code</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none uppercase"
                  placeholder="SAVE20"
                  required
                  disabled={!!editing}
                  data-testid="discount-code-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Type</Label>
                  <select
                    value={form.discount_type}
                    onChange={(e) => setForm(p => ({ ...p, discount_type: e.target.value }))}
                    className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] p-2 text-white"
                    data-testid="discount-type-select"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div>
                  <Label className="overline">Value</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.discount_value}
                    onChange={(e) => setForm(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                    data-testid="discount-value-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Max Uses</Label>
                  <Input
                    type="number" min="0"
                    value={form.max_uses || ''}
                    onChange={(e) => setForm(p => ({ ...p, max_uses: e.target.value ? parseInt(e.target.value) : null }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                    placeholder="Unlimited"
                    data-testid="discount-max-uses-input"
                  />
                </div>
                <div>
                  <Label className="overline">Min Purchase ($)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.min_purchase}
                    onChange={(e) => setForm(p => ({ ...p, min_purchase: parseFloat(e.target.value) || 0 }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                    data-testid="discount-min-purchase-input"
                  />
                </div>
              </div>
              <div>
                <Label className="overline">Expires At</Label>
                <Input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setForm(p => ({ ...p, expires_at: e.target.value }))}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                  data-testid="discount-expires-input"
                />
              </div>
              <Button type="submit" className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="discount-submit-btn">
                {editing ? 'UPDATE CODE' : 'CREATE CODE'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {discountCodes.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <Tag className="w-12 h-12 mx-auto text-[#A3A3A3] mb-4" />
          <p className="text-[#A3A3A3]">No discount codes yet. Create your first code!</p>
        </div>
      ) : (
        <div className="bg-[#141414] border border-[#262626]">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Uses</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {discountCodes.map(discount => (
                <tr key={discount.id} data-testid={`discount-row-${discount.id}`}>
                  <td className="font-mono font-bold">{discount.code}</td>
                  <td>
                    {discount.discount_type === 'percentage' ? (
                      <span className="flex items-center gap-1">
                        <Percent className="w-3 h-3 text-[#FF3B30]" /> {discount.discount_value}%
                      </span>
                    ) : (
                      <span>${discount.discount_value.toFixed(2)}</span>
                    )}
                  </td>
                  <td className="font-mono">{discount.uses || 0}{discount.max_uses ? `/${discount.max_uses}` : ''}</td>
                  <td>
                    <button
                      onClick={() => toggleActive(discount)}
                      className={`px-2 py-1 text-xs font-mono ${discount.is_active ? 'bg-[#22C55E]/20 text-[#22C55E]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'}`}
                      data-testid={`toggle-discount-${discount.id}`}
                    >
                      {discount.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </button>
                  </td>
                  <td>
                    <Button variant="ghost" size="icon"
                      onClick={() => handleDelete(discount.id)}
                      className="rounded-none text-[#FF3B30]"
                      data-testid={`delete-discount-${discount.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
